import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';

// واجهة الثيم
export interface Theme {
  id: string;
  name: string;
  direction: 'rtl' | 'ltr';
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  isDark: boolean;
}

// الثيمات المتاحة
export const availableThemes: Theme[] = [
  {
    id: 'default',
    name: 'الثيم الافتراضي',
    direction: 'rtl',
    primaryColor: '#0d51d6',
    secondaryColor: '#dc004e',
    fontFamily: 'Cairo, sans-serif',
    isDark: false
  },
  {
    id: 'dark',
    name: 'الوضع الداكن',
    direction: 'rtl',
    primaryColor: '#667eea',
    secondaryColor: '#f093fb',
    fontFamily: 'Cairo, sans-serif',
    isDark: true
  },
  {
    id: 'green',
    name: 'الثيم الأخضر',
    direction: 'rtl',
    primaryColor: '#4caf50',
    secondaryColor: '#ff9800',
    fontFamily: 'Cairo, sans-serif',
    isDark: false
  }
];

// إنشاء كاش للـ RTL
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// إنشاء كاش للـ LTR
const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

interface ThemeManagerProps {
  children: React.ReactNode;
  initialThemeId?: string;
}

const ThemeManager: React.FC<ThemeManagerProps> = ({
  children,
  initialThemeId = 'default'
}) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(
    availableThemes.find(theme => theme.id === initialThemeId) || availableThemes[0]
  );

  // إنشاء الثيم باستخدام البيانات الحالية
  const theme = createTheme({
    direction: currentTheme.direction,
    typography: {
      fontFamily: currentTheme.fontFamily,
    },
    palette: {
      mode: currentTheme.isDark ? 'dark' : 'light',
      primary: {
        main: currentTheme.primaryColor,
        light: currentTheme.isDark ? '#8b9cf9' : '#5a7de8',
        dark: currentTheme.isDark ? '#4a5fd8' : '#0a3bb5',
        contrastText: '#ffffff',
      },
      secondary: {
        main: currentTheme.secondaryColor,
        light: currentTheme.isDark ? '#f5b3fc' : '#f3a0f9',
        dark: currentTheme.isDark ? '#d870e8' : '#c850d8',
        contrastText: '#ffffff',
      },
      ...(currentTheme.isDark && {
        background: {
          default: '#0a0e27',
          paper: '#151a30',
        },
        text: {
          primary: '#ffffff',
          secondary: '#b0b8d4',
        },
        divider: 'rgba(255, 255, 255, 0.12)',
        action: {
          active: '#ffffff',
          hover: 'rgba(255, 255, 255, 0.08)',
          selected: 'rgba(255, 255, 255, 0.16)',
          disabled: 'rgba(255, 255, 255, 0.3)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
        },
      }),
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            textAlign: currentTheme.direction === 'rtl' ? 'right' : 'left',
            ...(currentTheme.isDark && {
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
              borderRadius: 12,
            }),
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            textAlign: currentTheme.direction === 'rtl' ? 'right' : 'left',
            ...(currentTheme.isDark && {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
              },
            }),
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
              borderRadius: 12,
            }),
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
          contained: {
            ...(currentTheme.isDark && {
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
              },
            }),
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
            }),
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...(currentTheme.isDark && {
              backgroundColor: '#151a30',
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
            }),
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              '& .MuiTableCell-root': {
                backgroundColor: '#1e2538',
                color: '#ffffff !important',
                fontWeight: 700,
              },
            }),
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
              },
            }),
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#ffffff',
              borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            }),
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              borderRadius: 8,
              color: '#ffffff',
            }),
          }
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: 'inherit',
            }),
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#b0b8d4',
              '&.Mui-focused': {
                color: currentTheme.primaryColor,
              },
            }),
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: currentTheme.primaryColor,
              },
            }),
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#ffffff',
            }),
          },
          icon: {
            ...(currentTheme.isDark && {
              color: '#b0b8d4',
            }),
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.12)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.3)',
                },
              },
            }),
          }
        }
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            ...(currentTheme.isDark && {
              color: '#ffffff',
            }),
          },
          secondary: {
            ...(currentTheme.isDark && {
              color: '#b0b8d4',
            }),
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#b0b8d4',
            }),
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              color: '#b0b8d4',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }),
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            ...(currentTheme.isDark && {
              borderColor: 'rgba(255, 255, 255, 0.12)',
            }),
          }
        }
      },
    }
  });

  // تحديث الثيم
  const changeTheme = (themeId: string) => {
    const newTheme = availableThemes.find(theme => theme.id === themeId);
    if (newTheme) {
      setCurrentTheme(newTheme);
      localStorage.setItem('selectedTheme', themeId);
    }
  };

  // تحميل الثيم المحفوظ عند بدء التشغيل
  useEffect(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme) {
      changeTheme(savedTheme);
    }
  }, []);

  // إضافة وظيفة تغيير الثيم إلى النافذة العالمية للوصول إليها من أي مكان
  useEffect(() => {
    (window as any).changeTheme = changeTheme;
  }, []);

  return (
    <CacheProvider value={currentTheme.direction === 'rtl' ? cacheRtl : cacheLtr}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  );
};

export default ThemeManager;
